import { pick } from 'lodash';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //

const ActionTypes = {
  SET_INITIAL_VALUES: 'app/AssociatedListingPage/SET_INITIAL_VALUES',

  FETCH_LISTING_REQUEST: 'app/AssociatedListingPage/FETCH_LISTING_REQUEST',
  FETCH_LISTING_SUCCESS: 'app/AssociatedListingPage/FETCH_LISTING_SUCCESS',
  FETCH_LISTING_ERROR: 'app/AssociatedListingPage/FETCH_LISTING_ERROR',
};

// ================ Initial State ================ //

const initialState = {
  fetchListingInProgress: false,
  fetchListingError: null,
};

// ================ Reducers ================ //

const AssociatedListingPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case ActionTypes.FETCH_LISTING_REQUEST:
      return { ...state, fetchListingInProgress: true, fetchListingError: null };
    case ActionTypes.FETCH_LISTING_SUCCESS:
      return {
        ...state,
        fetchListingInProgress: false,
        fetchListingError: null,
      };
    case ActionTypes.FETCH_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    default:
      return state;
  }
};

// ================ Selectors ================ //

export const fetchListingInProgressSelector = state =>
  state.AssociatedListingPage.fetchListingInProgress;

// ================ Actions Creators ================ //
export const setInitialValues = initialValues => ({
  type: ActionTypes.SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchListingRequest = () => ({ type: ActionTypes.FETCH_LISTING_REQUEST });
const fetchListingSuccess = () => ({ type: ActionTypes.FETCH_LISTING_SUCCESS });
const fetchListingError = error => ({
  type: ActionTypes.FETCH_LISTING_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

export const fetchAssociatedListing = (id, config) => async (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  dispatch(fetchListingRequest());
  try {
    const params = {
      id,
      include: ['author', 'author.profileImage', 'author.publicData', 'images', 'currentStock'],
      'fields.image': [
        'variants.default',
        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',

        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,

        // Social media
        'variants.facebook',
        'variants.twitter',

        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    };
    const response = await sdk.listings.show(params);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(fetchListingSuccess());
    return response;
  } catch (e) {
    dispatch(fetchListingError(storableError(e)));
  }
};

// Thunks
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { id } = params || {};
  if (id) {
    dispatch(fetchAssociatedListing(id, config));
  }
  const currentUser = await dispatch(fetchCurrentUser({ enforce: true }));
  const currentUserId = currentUser?.id;

  return currentUserId;
};

export default AssociatedListingPageReducer;
